<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { computed, ref, unref, useId, watch, type MaybeRef } from "vue"
import { useI18n } from "vue-i18n"
import { useConfirm } from "primevue"
import { useToast } from "primevue/usetoast"
import type Plotly from "plotly.js-dist-min"
import { ServiceError } from "@core/exceptions"
import { useFetchWrapper } from "@core/composables/useFetchWrapper"
import { useBuildingStore } from "@core/stores/building-v2.store"
import { useConfigStore } from "@core/stores/config.store"
import { useSourceStore } from "@core/stores/source.store"
import { useAlarmeStore } from "@modules/energy-manager/stores/alarme.store"
import {
    ALARM_THRESHOLD_CHART_COLORS,
    ALARM_THRESHOLD_PLOTLY_MARKER,
} from "@modules/energy-manager/constants/alarme"
import type {
    AlarmConfig,
    ThresholdPoint,
} from "@modules/energy-manager/types/alarme"

// ================================
// INTERNATIONALIZATION
// ================================
const { t } = useI18n({
    messages: {
        fr: {
            alarm: {
                seriesConsigne: "Consigne",
                seriesLimHaute: "Limite haute",
                seriesLimBasse: "Limite basse",
                save: "Sauvegarder",
                cancel: "Annuler",
                saveError: "La sauvegarde du profil a échoué.",
                toggleError: "La mise à jour de l’activation a échoué.",
                deleteError: "La suppression du profil a échoué.",
                deleteToastSummary: "Alarmes",
                deleteSuccessDetail:
                    "Le profil d’alarme pour « {name} » a été supprimé.",
                activated: "Activée",
                delete: "Supprimer",
                deleteConfirmTitle: "Supprimer le profil d’alarme ?",
                deleteConfirmMessage:
                    "Le profil d'alarme pour « {name} » sera supprimé. Cette action est irréversible.",
            },
        },
    },
})

// ================================
// PROPS & EMITS
// ================================
const props = defineProps<{
    type: "building" | "source"
    itemId: string
    variable: string
    /** Unité métier (`ConfigVariable.unit`) pour l’axe Y du graphique. */
    variableUnit?: string
    points: ThresholdPoint[]
    alarm?: Record<string, boolean | number | string>
}>()

// ================================
// ÉTAT LOCAL & STORES
// ================================
const alarmeStore = useAlarmeStore()
const buildingStore = useBuildingStore()
const sourceStore = useSourceStore()
const configStore = useConfigStore()
const confirm = useConfirm()
const toast = useToast()
/** Groupe PrimeVue Confirm : une instance par ligne (liste de variables). */
const confirmGroupId = useId()

// ================================
// AFFICHAGE VARIABLE
// ================================
/** Libellé métier (`ConfigVariable.name`) pour `variable` (`ConfigVariable.type`). */
const variableDisplayName = computed(
    () => configStore.getVariableByType(props.variable)?.name ?? props.variable
)

/** Config variable (icône / couleur) pour l’en-tête de ligne. */
const variableConfig = computed(() =>
    configStore.getVariableByType(props.variable)
)

// ================================
// ÉTAT ÉDITION & GRAPHIQUE
// ================================
const isActive = ref(Boolean(props.alarm?.active))
const mode = ref<"none" | "edit" | "import" | "auto">("none")
/** Vrai après une génération auto réussie dans la session (active « Sauvegarder »). */
const hasAutoGeneratePreview = ref(false)
/** Cible DOM unique pour Teleport des boutons sous le graphique (mode auto). */
const autoGenerateChartActionsId = `alarm-auto-chart-actions-${useId()}`
/** Remonte le formulaire seuils pour ré-inférer semaine/week-end et jour/nuit après annulation. */
const thresholdFormRemountKey = ref(0)
const localPoints = ref<ThresholdPoint[]>([...props.points])
/** Référence au formulaire seuils (validation ordre des limites). */
const alarmFormRef = ref<{
    isValidThresholdForm: MaybeRef<boolean>
} | null>(null)
const {
    fetch: persistAlarmActive,
    isFetching: isTogglingAlarmActive,
    error: toggleAlarmError,
} = useFetchWrapper(async () => {
    await alarmeStore.updateAlarm(
        { scope: props.type, id: props.itemId },
        props.variable,
        { active: isActive.value }
    )
})

const {
    fetch: persistThresholdEdit,
    isFetching: isSavingThresholdEdit,
    error: thresholdEditError,
} = useFetchWrapper(async () => {
    await alarmeStore.updateThreshold(
        { scope: props.type, id: props.itemId },
        props.variable,
        localPoints.value
    )
})

const editSaveErrorMessage = computed(() => {
    const err = thresholdEditError.value
    if (!err) return ""
    return err instanceof ServiceError ? err.message : t("alarm.saveError")
})

const toggleAlarmErrorMessage = computed(() => {
    const err = toggleAlarmError.value
    if (!err) return ""
    return err instanceof ServiceError ? err.message : t("alarm.toggleError")
})

watch(
    () => props.alarm?.active,
    (active) => {
        if (active !== undefined) {
            isActive.value = Boolean(active)
        }
    }
)

/** Sync switch quand le store est mis à jour hors ligne (ex. génération groupée + activation). */
watch(
    () =>
        alarmeStore.getAlarmConfigFromClient(
            { scope: props.type, id: props.itemId },
            props.variable
        ).active,
    (active) => {
        if (active !== undefined) {
            isActive.value = Boolean(active)
        }
    }
)

watch(
    () => props.points,
    (next) => {
        localPoints.value = [...next]
    },
    { deep: true }
)

watch(mode, (m) => {
    if (m !== "auto") {
        hasAutoGeneratePreview.value = false
    }
})

/**
 * Ordre strict des seuils (aligné formulaire seuils).
 *
 * @param points points locaux
 */
function getAreThresholdPointsOrdered(points: ThresholdPoint[]): boolean {
    if (!points.length) return false
    return points.every(
        (p) => p.limbasse < p.consigne && p.consigne < p.limhaute
    )
}

const getCanSaveAutoGenerate = computed(
    () =>
        mode.value === "auto" &&
        hasAutoGeneratePreview.value &&
        getAreThresholdPointsOrdered(localPoints.value) &&
        !isSavingThresholdEdit.value
)

/**
 * Config d’alarme pour cette variable, lue sur le store bâtiment/source.
 * Lecture directe (pas via `alarmeStore.getAlarmConfigFromClient`) pour que les
 * computed des icônes se mettent à jour quand les options changent dans le panneau.
 */
const alarmConfigReactive = computed((): AlarmConfig => {
    if (props.type === "building") {
        return { ...(buildingStore.building?.alarme?.[props.variable] ?? {}) }
    }
    const src = sourceStore.sources.find(
        (s) => String(s._id_source) === String(props.itemId)
    )
    return { ...(src?.alarme?.[props.variable] ?? {}) }
})

/**
 * Interprète une valeur API / store comme booléen (aligné AlarmOptions).
 *
 * @param value valeur brute
 */
function toAlarmBool(value: unknown): boolean {
    if (value === true || value === 1) return true
    if (value === false || value === 0) return false
    if (typeof value === "string") {
        const s = value.toLowerCase()
        if (s === "true" || s === "1") return true
        if (s === "false" || s === "0") return false
    }
    return Boolean(value)
}

/** Cloche : alarme activée et surveillance au moins d’un seuil (haut ou bas). */
const getShowAlarmThresholdIcon = computed(() => {
    if (!isActive.value) return false
    const cfg = alarmConfigReactive.value
    return toAlarmBool(cfg.high_active) || toAlarmBool(cfg.low_active)
})

/** Enveloppe : alarme activée et notification email (résolution ou déclenchement). */
const getShowAlarmNotifyIcon = computed(() => {
    if (!isActive.value) return false
    const cfg = alarmConfigReactive.value
    return toAlarmBool(cfg.notify_resolved) || toAlarmBool(cfg.notify_triggered)
})

const getChartData = computed<Plotly.Data[]>(() => {
    const xs = localPoints.value.map((point) => point.timestamp)
    return [
        {
            type: "scatter" as const,
            mode: "lines+markers" as const,
            name: t("alarm.seriesConsigne"),
            x: xs,
            y: localPoints.value.map((point) => point.consigne),
            line: { color: ALARM_THRESHOLD_CHART_COLORS.consigne },
            marker: {
                ...ALARM_THRESHOLD_PLOTLY_MARKER,
                color: ALARM_THRESHOLD_CHART_COLORS.consigne,
            },
        },
        {
            type: "scatter" as const,
            mode: "lines+markers" as const,
            name: t("alarm.seriesLimHaute"),
            x: xs,
            y: localPoints.value.map((point) => point.limhaute),
            line: { color: ALARM_THRESHOLD_CHART_COLORS.limhaute },
            marker: {
                ...ALARM_THRESHOLD_PLOTLY_MARKER,
                color: ALARM_THRESHOLD_CHART_COLORS.limhaute,
            },
        },
        {
            type: "scatter" as const,
            mode: "lines+markers" as const,
            name: t("alarm.seriesLimBasse"),
            x: xs,
            y: localPoints.value.map((point) => point.limbasse),
            line: { color: ALARM_THRESHOLD_CHART_COLORS.limbasse },
            marker: {
                ...ALARM_THRESHOLD_PLOTLY_MARKER,
                color: ALARM_THRESHOLD_CHART_COLORS.limbasse,
            },
        },
    ]
})

async function onToggleAlarm(value: boolean) {
    const previous = isActive.value
    isActive.value = value
    await persistAlarmActive()
    if (toggleAlarmError.value) {
        isActive.value = previous
    }
}

function updateAlarmToggle(value: boolean) {
    void onToggleAlarm(value)
}

const getCanSaveThresholdEdit = computed(() => {
    const form = alarmFormRef.value
    if (!form) return false
    return unref(form.isValidThresholdForm)
})

async function onSaveThresholdEdit() {
    if (!getCanSaveThresholdEdit.value) return
    await persistThresholdEdit()
    if (!thresholdEditError.value) {
        mode.value = "none"
    }
}

function clearToggleAlarmError() {
    toggleAlarmError.value = null
}

function clearThresholdEditError() {
    thresholdEditError.value = null
}

function onCancelThresholdEdit() {
    thresholdEditError.value = null
    localPoints.value = [...props.points]
    thresholdFormRemountKey.value += 1
    mode.value = "none"
}

async function onSaveImport() {
    if (!localPoints.value.length) return
    await persistThresholdEdit()
    if (!thresholdEditError.value) {
        mode.value = "none"
    }
}

function onCancelImport() {
    thresholdEditError.value = null
    localPoints.value = [...props.points]
    mode.value = "none"
}

function onAutoGenerateChangePoints(pts: ThresholdPoint[]) {
    localPoints.value = pts
    hasAutoGeneratePreview.value = true
}

async function onSaveAutoGenerate() {
    if (!getCanSaveAutoGenerate.value) return
    await persistThresholdEdit()
    if (!thresholdEditError.value) {
        hasAutoGeneratePreview.value = false
        mode.value = "none"
    }
}

function onCancelAutoGenerate() {
    thresholdEditError.value = null
    localPoints.value = [...props.points]
    hasAutoGeneratePreview.value = false
    mode.value = "none"
}

/**
 * Fréquence API `temp` pour l’auto-génération (legacy : 7 → quotidien, 168 → horaire).
 */
const thresholdSeriesFreq = computed((): "hourly" | "daily" | null => {
    const n = localPoints.value.length
    if (n === 168) return "hourly"
    if (n === 7) return "daily"
    return null
})

/**
 * Suppression confirmée : optimistic update dans le store, toast succès / erreur (rollback géré par le store).
 */
async function onRemoveThresholdConfirmed(): Promise<void> {
    try {
        await alarmeStore.removeThreshold(
            { scope: props.type, id: props.itemId },
            props.variable
        )
        toast.add({
            group: "notifications",
            severity: "success",
            summary: t("alarm.deleteToastSummary"),
            detail: t("alarm.deleteSuccessDetail", {
                name: variableDisplayName.value,
            }),
            life: 4000,
        })
    } catch (err) {
        const detail =
            err instanceof ServiceError
                ? err.message
                : err instanceof Error
                  ? err.message
                  : t("alarm.deleteError")
        toast.add({
            group: "notifications",
            severity: "error",
            summary: t("alarm.deleteToastSummary"),
            detail,
            life: 6000,
        })
    }
}

function onConfirmRemoveThreshold() {
    confirm.require({
        group: confirmGroupId,
        header: t("alarm.deleteConfirmTitle"),
        message: t("alarm.deleteConfirmMessage", {
            name: variableDisplayName.value,
        }),
        icon: "pi pi-exclamation-triangle",
        rejectProps: {
            label: t("alarm.cancel"),
            outlined: true,
        },
        acceptProps: {
            label: t("alarm.delete"),
            severity: "danger",
            icon: "pi pi-trash",
        },
        accept: () => {
            void onRemoveThresholdConfirmed()
        },
    })
}

defineExpose({ updateAlarmToggle })
</script>

<template>
    <div class="item-alarm">
        <BaseMessage
            v-if="toggleAlarmErrorMessage"
            severity="error"
            :closable="true"
            @close="clearToggleAlarmError"
        >
            {{ toggleAlarmErrorMessage }}
        </BaseMessage>
        <BaseAccordion value="0">
            <BaseAccordionPanel value="0">
                <BaseAccordionHeader>
                    <div
                        class="flex md:flex-row flex-col w-full min-w-0 flex-wrap md:items-center gap-3"
                    >
                        <div
                            class="flex shrink-0 items-center gap-3"
                            @click.stop
                        >
                            <BaseToggleSwitch
                                :model-value="isActive"
                                :disabled="isTogglingAlarmActive"
                                @update:model-value="
                                    onToggleAlarm(Boolean($event))
                                "
                            />
                            <div
                                class="flex shrink-0 items-center gap-1.5 text-sm text-slate-600"
                            >
                                <span>{{ t("alarm.activated") }}</span>
                                <i
                                    v-if="getShowAlarmThresholdIcon"
                                    class="pi pi-bell text-[0.875rem] text-slate-500"
                                    aria-hidden="true"
                                />
                                <i
                                    v-if="getShowAlarmNotifyIcon"
                                    class="pi pi-envelope text-[0.875rem] text-slate-500"
                                    aria-hidden="true"
                                />
                            </div>
                        </div>
                        <div class="flex min-w-0 flex-1 items-center gap-2.5">
                            <span
                                class="relative flex size-9 shrink-0 items-center justify-center overflow-hidden rounded-md bg-slate-50 ring-1 ring-inset ring-slate-200/70"
                            >
                                <span
                                    v-if="variableConfig?.color"
                                    class="pointer-events-none absolute inset-0 opacity-[0.18]"
                                    :style="{
                                        backgroundColor: String(
                                            variableConfig.color
                                        ),
                                    }"
                                />
                                <BaseIcon
                                    v-if="variableConfig?.icon"
                                    class="relative z-[1]"
                                    :icon="String(variableConfig.icon)"
                                    :color="variableConfig.color || '#334155'"
                                    size="1.125rem"
                                />
                                <i
                                    v-else
                                    class="pi pi-chart-line relative z-[1] text-[1.05rem] text-slate-500"
                                    aria-hidden="true"
                                />
                            </span>
                            <span
                                class="min-w-0 flex-1 truncate font-medium text-primary"
                            >
                                {{ variableDisplayName }}
                            </span>
                        </div>
                        <div
                            class="md:ml-auto mx-auto md:mr-4 shrink-0"
                            @click.stop
                        >
                            <AlarmVariableRowActions
                                :type="type"
                                @edit="mode = 'edit'"
                                @import="mode = 'import'"
                                @auto-generate="mode = 'auto'"
                                @delete="onConfirmRemoveThreshold"
                            />
                        </div>
                    </div>
                </BaseAccordionHeader>
                <BaseAccordionContent
                    :pt="{
                        content: 'flex flex-col gap-6',
                    }"
                >
                    <div v-if="mode === 'edit'" class="flex flex-col gap-4">
                        <AlarmForm
                            :key="thresholdFormRemountKey"
                            ref="alarmFormRef"
                            :points="localPoints"
                            :variable-unit="variableUnit"
                            @change-points="localPoints = $event"
                        />
                        <BaseMessage
                            v-if="editSaveErrorMessage"
                            severity="error"
                            :closable="true"
                            @close="clearThresholdEditError"
                        >
                            {{ editSaveErrorMessage }}
                        </BaseMessage>
                        <div class="flex flex-wrap justify-center gap-2">
                            <BaseButton
                                type="button"
                                outlined
                                severity="secondary"
                                :disabled="isSavingThresholdEdit"
                                :label="t('alarm.cancel')"
                                @click="onCancelThresholdEdit"
                            />
                            <BaseButton
                                type="button"
                                :loading="isSavingThresholdEdit"
                                :disabled="
                                    isSavingThresholdEdit ||
                                    !getCanSaveThresholdEdit
                                "
                                :label="t('alarm.save')"
                                @click="onSaveThresholdEdit"
                            />
                        </div>
                    </div>
                    <div
                        v-else-if="mode === 'import'"
                        class="flex flex-col gap-4"
                    >
                        <BaseMessage
                            v-if="editSaveErrorMessage"
                            severity="error"
                            :closable="true"
                            @close="clearThresholdEditError"
                        >
                            {{ editSaveErrorMessage }}
                        </BaseMessage>
                        <AlarmFormImport
                            :type="type"
                            :item-id="itemId"
                            :variable="variable"
                            :saving="isSavingThresholdEdit"
                            @change-points="localPoints = $event"
                            @save="onSaveImport"
                            @cancel="onCancelImport"
                        />
                    </div>
                    <AlarmFormAutoGenerate
                        v-else-if="mode === 'auto'"
                        :type="type"
                        :item-id="itemId"
                        :variable="variable"
                        :freq="thresholdSeriesFreq"
                        :teleport-target="`#${autoGenerateChartActionsId}`"
                        :can-save="getCanSaveAutoGenerate"
                        :saving="isSavingThresholdEdit"
                        @change-points="onAutoGenerateChangePoints"
                        @confirm="onSaveAutoGenerate"
                        @cancel="onCancelAutoGenerate"
                    />
                    <AlarmPlotlyChart
                        v-if="localPoints.length"
                        :chart-data="getChartData"
                        :y-axis-unit="variableUnit"
                        :frequency="thresholdSeriesFreq ?? 'auto'"
                        :disable-zoom="true"
                    />
                    <div
                        v-if="mode === 'auto'"
                        :id="autoGenerateChartActionsId"
                        class="min-h-0 w-full"
                    />
                    <AlarmOptions
                        class="md:px-8 md:py-6"
                        :type="type"
                        :item-id="itemId"
                        :variable="variable"
                        :alarm="alarm"
                    />
                </BaseAccordionContent>
            </BaseAccordionPanel>
        </BaseAccordion>
        <BaseConfirmDialog :group="confirmGroupId" />
    </div>
</template>
